/**
 * Centralized Status Utility Functions
 * 
 * Consolidates all status-related styling and formatting logic
 * to eliminate 200+ lines of duplicate code across components
 */

// import { StatusConfig, TaskTypeConfig } from '../types/container'
import { statusConfig, taskTypeConfig } from '../data/dispatchData'
import { daysUntilLfd } from './lfdDate'

interface StatusStyleResult {
  bgColor: string
  textColor: string
  borderColor: string
  label: string
}

/**
 * Get consistent status styling across all components
 */
export const getStatusStyles = (status: string): StatusStyleResult => {
  const config = statusConfig[status]
  return {
    bgColor: config?.bgColor || 'bg-gray-100',
    textColor: config?.textColor || 'text-gray-700',
    borderColor: 'border-gray-200', // config?.borderColor || 'border-gray-200',
    label: config?.label || status
  }
}

/**
 * Get task type styling
 */
export const getTaskTypeStyles = (taskType: string): StatusStyleResult => {
  const config = taskTypeConfig[taskType]
  return {
    bgColor: config?.bgColor || 'bg-gray-100',
    textColor: config?.textColor || 'text-gray-700',
    borderColor: 'border-gray-200', // config?.borderColor || 'border-gray-200',
    label: config?.label || taskType
  }
}

/**
 * Generate CSS classes for status badges
 */
export const getStatusBadgeClasses = (status: string): string => {
  const styles = getStatusStyles(status)
  return `${styles.bgColor} ${styles.textColor} border-0 rounded-full text-xs px-2 py-0.5 font-medium`
}

/**
 * Generate CSS classes for task type badges
 */
export const getTaskTypeBadgeClasses = (taskType: string): string => {
  const styles = getTaskTypeStyles(taskType)
  return `${styles.bgColor} ${styles.textColor} border-0 rounded-full text-xs px-2 py-0.5 font-medium`
}

/**
 * Get border color for task cards based on status
 */
export const getTaskBorderColor = (status: string): string => {
  const styles = getStatusStyles(status)
  return styles.borderColor.replace('border-', 'border-')
}

/**
 * Legacy compatibility - maps old function signatures
 * TODO: Remove after migration is complete
 */
export const getStatusColor = (status: string): string => {
  const styles = getStatusStyles(status)
  return `${styles.textColor} ${styles.bgColor}`
}

/**
 * Utility for driver status badges
 */
export const getDriverStatusBadge = (status?: string, hasInProgress?: boolean) => {
  // If no status provided, use the old logic based on task progress
  if (!status) {
    if (hasInProgress) {
      return {
        className: 'inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium',
        indicator: 'w-2 h-2 bg-green-500 rounded-full animate-pulse',
        label: 'Active'
      }
    }
    return {
      className: 'inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium',
      indicator: 'w-2 h-2 bg-gray-400 rounded-full',
      label: 'Available'
    }
  }
  
  // New logic based on actual driver status
  switch (status) {
    case 'on-duty':
      return {
        className: 'inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium',
        indicator: 'w-2 h-2 bg-green-500 rounded-full',
        label: 'On Duty'
      }
    case 'off-duty':
      return {
        className: 'inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium',
        indicator: 'w-2 h-2 bg-gray-400 rounded-full',
        label: 'Off Duty'
      }
    case 'on-leave':
      return {
        className: 'inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium',
        indicator: 'w-2 h-2 bg-yellow-500 rounded-full',
        label: 'On Leave'
      }
    case 'on-task':
      return {
        className: 'inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium',
        indicator: 'w-2 h-2 bg-blue-500 rounded-full animate-pulse',
        label: 'On Task'
      }
    default:
      return {
        className: 'inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs font-medium',
        indicator: 'w-2 h-2 bg-gray-400 rounded-full',
        label: status || 'Unknown'
      }
  }
}

/**
 * LFD urgency calculation and styling
 */
export const getLFDUrgency = (lfdDate: string): string => {
  if (!lfdDate || lfdDate === "Pending") return "normal"

  // Whole calendar days. Measuring from `new Date()` instead mixed a day with
  // a time-of-day and could push the box a day either way depending on when
  // the dispatcher happened to look at the screen.
  const diffDays = daysUntilLfd(lfdDate)
  if (diffDays === null) return "normal"

  if (diffDays < 0) return "overdue"
  if (diffDays <= 1) return "urgent"
  if (diffDays <= 3) return "moderate"
  return "normal"
}

/**
 * Get LFD styling based on urgency
 */
export const getLFDStyles = (urgency: string): string => {
  switch (urgency) {
    case "overdue":
      return "text-red-600 bg-red-50 border border-red-200 font-semibold"
    case "urgent":
      return "text-orange-600 bg-orange-50 border border-orange-200 font-semibold"
    case "moderate":
      return "text-yellow-600 bg-yellow-50 border border-yellow-200"
    default:
      return "text-gray-600 bg-gray-50"
  }
}